import { useQuery } from '@tanstack/react-query';
import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { api } from '~/api';
import type { ProductData } from '~/store/models/modelTypes';

interface ProductsResponse {
  products: ProductData[];
}

export const useProducts = (solutionId?: string) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const effectiveSolutionId = solutionId ?? solution?.id;

  return useQuery({
    queryKey: ['products', effectiveSolutionId],
    queryFn: async () => {
      if (!effectiveSolutionId) return [];
      const data: ProductsResponse = await api.getData2('/products', {
        solution: effectiveSolutionId,
      });
      return data.products;
    },
    enabled: !!effectiveSolutionId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useProductById = (productId: string) => {
  const solution = useStoreState((state) => state.auth.solution);

  const { data: products } = useProducts(solution?.id);

  return useMemo(
    () => products?.find((p) => p.id === productId) || null,
    [products, productId]
  );
};

export const useSolutionProducts = (solutionId?: string) => {
  const { data: products = [], ...rest } = useProducts(solutionId);

  const sortedProducts = useMemo(
    () =>
      [...products].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      ),
    [products]
  );

  return { data: sortedProducts, ...rest };
};

export const useMarketplaceProducts = (solutionId?: string) => {
  const { data: products = [], ...rest } = useSolutionProducts(solutionId);

  const marketplaceProducts = useMemo(
    () => products.filter((p) => p.marketplace === true),
    [products]
  );

  return { data: marketplaceProducts, ...rest };
};

export const useAllSolutionProducts = (solutionId?: string) => {
  const solutions = useStoreState((state: any) => state.auth.solutions);

  return useMemo(() => {
    if (!solutionId || !solutions) return [];

    // Find the solution
    const solution = solutions.find((s: any) => s.id === solutionId);
    if (!solution) return [];

    // Extract products from division roles
    const productIds = new Set<string>();
    const productsMap = new Map<string, ProductData>();

    const roles = solution.roles || [];
    roles.forEach((role: any) => {
      const divisionProducts = role?.division?.products || [];
      divisionProducts.forEach((p: any) => {
        if (p && p.id && !productIds.has(p.id)) {
          productIds.add(p.id);
          productsMap.set(p.id, p);
        }
      });
    });

    // Convert to array and sort by name
    const products = Array.from(productsMap.values());
    return products.sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    );
  }, [solutionId, solutions]);
};

export const useCommodityFilteredProducts = (solutionId?: string) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const { data: products = [], ...rest } = useSolutionProducts(solutionId);

  const filteredProducts = useMemo(() => {
    const isCommodityMode = solution?.settings?.matchinglevel === 'commodity';

    if (!isCommodityMode) {
      return products;
    }

    // Deduplicate by commodity.id when in commodity mode
    const commodityMap = new Map<string, ProductData>();
    products.forEach((p: any) => {
      const commodityId = p?.commodity?.id;
      if (commodityId && !commodityMap.has(commodityId)) {
        commodityMap.set(commodityId, p);
      }
    });

    return Array.from(commodityMap.values());
  }, [products, solution]);

  return { data: filteredProducts, ...rest };
};

export const useSolutionDivisionProducts = (solutionId?: string) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const { data: allProducts = [] } = useProducts(solutionId);

  const divisionProducts = useMemo(() => {
    if (!solution) return [];

    const roles = solution.roles || [];

    // Extract unique product IDs from all divisions
    const productIds = new Set<string>();
    roles.forEach((role: any) => {
      const divisionProducts = role?.division?.products || [];
      divisionProducts.forEach((p: any) => {
        if (p?.id) {
          productIds.add(p.id);
        }
      });
    });

    // Merge with full product data
    const products: any[] = [];
    productIds.forEach((id: string) => {
      const fullProduct = allProducts.find((p: any) => p.id === id);
      if (fullProduct) {
        products.push(fullProduct);
      }
    });

    return products;
  }, [solution, allProducts]);

  return divisionProducts;
};

export const useSolutionTags = (solutionId?: string) => {
  const products = useSolutionDivisionProducts(solutionId);

  const tags = useMemo(() => {
    // Extract all tags from products
    const tagsMap = new Map<string, any>();
    products.forEach((product: any) => {
      const productTags = product?.tags || [];
      productTags.forEach((tag: any) => {
        if (tag && tag.id && !tagsMap.has(tag.id)) {
          tagsMap.set(tag.id, tag);
        }
      });
    });

    return Array.from(tagsMap.values());
  }, [products]);

  return tags;
};

export const useTradableProducts = (
  orderType: 'bid' | 'offer',
  solutionId?: string
) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const { data: allProducts = [] } = useProducts(solutionId);

  const tradableProducts = useMemo(() => {
    if (!solution) return [];

    const roles = solution.roles || [];

    // Filter roles by manager/trader AND division type based on orderType
    const tradableRoles = roles.filter((role: any) => {
      const hasTradeRole = ['manager', 'trader'].includes(role.role);
      if (!hasTradeRole) return false;

      const divisionType = role?.division?.divisiontype;
      if (orderType === 'bid') {
        return ['buyer', 'trader', 'broker'].includes(divisionType);
      } else {
        return ['seller', 'trader', 'broker'].includes(divisionType);
      }
    });

    // Extract products with division info
    const productsWithDivisions: any[] = [];
    tradableRoles.forEach((role: any) => {
      const division = role.division;
      const divisionProducts = division?.products || [];

      divisionProducts.forEach((p: any) => {
        productsWithDivisions.push({
          ...p,
          _division: {
            id: division.id,
            currency: division.currency,
            products: division.products,
          },
        });
      });
    });

    // Merge with full product data and attach division info
    const productMap = new Map<string, any>();

    productsWithDivisions.forEach((p: any) => {
      const fullProduct = allProducts.find((fp: any) => fp.id === p.id);
      if (fullProduct) {
        const pid = p.id;
        const divId = p._division.id;

        if (!productMap.has(pid)) {
          productMap.set(pid, {
            ...fullProduct,
            divs: {},
          });
        }

        const product = productMap.get(pid);
        product.divs[divId] = p._division;
      }
    });

    return Array.from(productMap.values());
  }, [solution, allProducts, orderType]);

  return tradableProducts;
};

export const useUserProductsTradeActions = () => {
  const solution = useStoreState((state: any) => state.auth.solution);

  const productsDict = useMemo(() => {
    if (!solution) return {};

    const roles = (solution.roles || []).filter((role: any) =>
      ['manager', 'trader'].includes(role.role)
    );

    const result: Record<string, any> = {};

    roles.forEach((item: any) => {
      const divisionType = item?.division?.divisiontype || '';
      const roleProducts = item?.division?.products || [];

      roleProducts.forEach((p: any) => {
        // Use id as the key since division products may not have p_id
        const pId = p.id;

        if (['buyer', 'trader', 'broker'].includes(divisionType)) {
          if (!result[pId]) result[pId] = { divisions: {} };
          result[pId].buy = p.id;
          result[pId].allowdecline = item.division.allowdecline;
          result[pId].divisions[item.division.id] = {
            id: item.division.id,
            name: item.division.name,
            warehouses: item.division.warehouses,
          };
        }
        if (['seller', 'trader', 'broker'].includes(divisionType)) {
          if (!result[pId]) result[pId] = { divisions: {} };
          result[pId].sell = p.id;
          result[pId].allowdecline = item.division.allowdecline;
          result[pId].divisions[item.division.id] = {
            id: item.division.id,
            name: item.division.name,
            warehouses: item.division.warehouses,
          };
        }
      });
    });

    return result;
  }, [solution]);

  return productsDict;
};

// tradeProduct + tradeActions: Used for trading (permissions, forms, buttons)
// May fall back to a variant product if user lacks base product permissions
export const useOrderTradeProduct = (order: any) => {
  const productsTradeActions = useUserProductsTradeActions();
  const baseProduct = useProductById(order?.product?.id);
  const { data: allProducts = [] } = useProducts();

  // Determine the required permission based on order type
  const requiredAction = order?.type === 'offer' ? 'buy' : 'sell';

  return useMemo(() => {
    // If base product has the required permission, use it
    if (baseProduct?.id) {
      const baseTradeActions = productsTradeActions?.[baseProduct.id];
      if (baseTradeActions?.[requiredAction]) {
        return { tradeProduct: baseProduct, tradeActions: baseTradeActions };
      }
    }

    // Check variants for one with the required permission
    for (const variant of order?.variants || []) {
      const variantProduct = allProducts.find(
        (p) => p.id === variant.product?.id
      );
      const variantActions = productsTradeActions?.[variant.product?.id];
      if (variantProduct && variantActions?.[requiredAction]) {
        return { tradeProduct: variantProduct, tradeActions: variantActions };
      }
    }

    // Fallback: base product with any trade actions
    if (baseProduct?.id) {
      const baseTradeActions = productsTradeActions?.[baseProduct.id];
      if (baseTradeActions?.buy || baseTradeActions?.sell) {
        return { tradeProduct: baseProduct, tradeActions: baseTradeActions };
      }
    }

    // Fallback: any variant with any trade actions
    for (const variant of order?.variants || []) {
      const variantProduct = allProducts.find(
        (p) => p.id === variant.product?.id
      );
      const variantActions = productsTradeActions?.[variant.product?.id];
      if (variantProduct && (variantActions?.buy || variantActions?.sell)) {
        return { tradeProduct: variantProduct, tradeActions: variantActions };
      }
    }

    // Last fallback: base product or first accessible variant for trading
    if (baseProduct) {
      return { tradeProduct: baseProduct, tradeActions: {} };
    }
    const accessibleVariant = order?.variants?.find((v: any) =>
      allProducts.some((p) => p.id === v.product?.id)
    );
    const variantProduct = accessibleVariant
      ? (allProducts.find((p) => p.id === accessibleVariant.product?.id) ??
        null)
      : null;
    return { tradeProduct: variantProduct, tradeActions: {} };
  }, [
    baseProduct,
    order?.variants,
    allProducts,
    productsTradeActions,
    requiredAction,
  ]);
};
